import * as Dialog from '@radix-ui/react-dialog';
import { Building2, ChevronsUpDown, Globe, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useAdministrationScopesOptional } from './AdministrationScopesProvider';
import { placeKey, type Place } from './place';
import { PlaceChooser } from './PlaceChooser';

/**
 * Inside the administration area, the scope row's slot shows the place being administered and
 * opens the chooser (decision 60). It never reads or changes the shell's active organization: the
 * place comes from the address, the chooser navigates, and on leaving the area the slot is the
 * scope row again. The label says «Amministri» so it can never be read as the organization the
 * modules work in — the condition the finance session set for FR-AD-1.
 */
export function AdminPlaceRow({ place }: { place: Place }) {
  const { t } = useTranslation('administration');
  const administration = useAdministrationScopesOptional();
  const [open, setOpen] = useState(false);
  const detail = administration?.places[placeKey(place)];
  const name = detail?.place.name ?? '…';
  const kind = place.kind === 'platform' ? null : t(`place.kind.${place.kind}`);
  const tenant = place.kind === 'organization' ? detail?.tenant?.name : undefined;
  const described = kind === null ? name : `${kind} ${name}`;

  return (
    <div className="shell-scope place-row" role="group" aria-label={t('place.rowLabel')}>
      <span className="shell-scope-label" aria-hidden="true">
        {t('place.rowLabel')}
      </span>
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Trigger asChild>
          <button
            type="button"
            className="place-chip"
            aria-label={t('place.change', { place: described })}
          >
            {place.kind === 'platform' ? (
              <Globe aria-hidden="true" className="text-selected-foreground size-4" />
            ) : (
              <Building2 aria-hidden="true" className="text-selected-foreground size-4" />
            )}
            <span className="place-chip-text">
              {kind !== null ? <span className="text-muted-foreground">{kind}</span> : null}
              <span className="font-semibold">{name}</span>
              {tenant !== undefined ? (
                <span className="place-chip-parent text-muted-foreground">· {tenant}</span>
              ) : null}
            </span>
            <ChevronsUpDown aria-hidden="true" className="text-muted-foreground size-3.5" />
          </button>
        </Dialog.Trigger>
        {/* Portalled INTO the shell rather than to <body>, so its phone layout follows the same
            `shell` container query as everything else and no width threshold lives outside
            shell.css. */}
        <Dialog.Portal container={document.querySelector<HTMLElement>('.app-shell') ?? undefined}>
          <Dialog.Overlay className="place-picker-veil" />
          <Dialog.Content className="place-picker" aria-describedby={undefined}>
            <span aria-hidden="true" className="place-picker-handle" />
            <div className="flex items-start justify-between gap-2 px-5 pt-4">
              <div>
                <Dialog.Title className="text-foreground text-lg font-bold">
                  {t('picker.title')}
                </Dialog.Title>
                <p className="text-muted-foreground mt-0.5 text-sm">{t('picker.hint')}</p>
              </div>
              <Dialog.Close asChild>
                <Button variant="ghost" size="icon" aria-label={t('picker.close')}>
                  <X />
                </Button>
              </Dialog.Close>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-3 pt-2 pb-4">
              <PlaceChooser current={place} onChosen={() => setOpen(false)} autoFocus />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      {tenant !== undefined ? (
        <span className="place-row-parent text-muted-foreground text-[13px]">
          {t('place.inTenant', { tenant })}
        </span>
      ) : null}
    </div>
  );
}
